import { useMemo, FC, useCallback } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { AvatarIcon } from 'components/Core/AvatarIcon/AvatarIcon';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { StatusCell } from 'components/Core/StatusCell';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { IPointListData } from 'stores/points/types';
import { pointsTableTranslations } from './translations';
import { PointsTableTransFields } from './types';
import { availableRoles, POINTS_TABLE_LIMIT } from './constants';
import styles from './pointsTable.module.scss';
import PointCategoryItem from 'components/Core/PointCategoryItem';

const PointsTable: FC = () => {
  const {
    pointsStore: { points, pointsQuery, getPointsData, changePointStatus },
    mainStore: { user },
  } = useStore();

  const { t } = useMyTranslations<PointsTableTransFields>([pointsTableTranslations.name]);

  const navigate = useNavigate();

  const { mutate } = useMutation(getPointsData, {
    mutationKey: 'getPointsData',
  });

  const onEditClick = useCallback((id: string) => {
    navigate(`${id}`);
  }, []);

  const disabled = useMemo(() => !user || !availableRoles.includes(user.role), [user?.role]);

  const columns: Column<IPointListData>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { isValidLocation, name, logoUrl },
          },
        }: CellProps<IPointListData>) => (
          <div className={styles.nameCell}>
            <div className={`${styles.verifiedStatus} ${isValidLocation ? styles.verified : styles.notVerified}`} />
            <div className={styles.avatar}>
              <AvatarIcon avatar={logoUrl} name='point image' size={56} styleProps={{ borderRadius: 8 }} />
              <p className={styles.name}>{name}</p>
            </div>
          </div>
        ),
        accessor: (row) => row.name,
        width: 300,
        sortType: 'apiSort',
      },
      {
        Header: t('categories'),
        id: 'categories',
        Cell: ({
          row: {
            original: { categories },
          },
        }: CellProps<IPointListData>) => (
          <div className={styles.categories}>
            {categories?.map((item) => (
              <PointCategoryItem key={item.id} category={item} />
            ))}
          </div>
        ),
        accessor: ({ categories }) => categories && categories.length,
        width: 140,
        sortType: 'apiSort',
      },
      {
        Header: t('address'),
        id: 'address',
        accessor: (row) => row.address,
        width: 120,
        sortType: 'apiSort',
      },
      {
        Header: t('owner'),
        id: 'owner',
        Cell: ({
          row: {
            original: { owner },
          },
        }: CellProps<IPointListData>) =>
          owner ? (
            <div className={styles.owner}>
              <p className={styles.ownerData}>{owner.name}</p>
              <p className={styles.ownerData}>{owner.email}</p>
            </div>
          ) : null,
        accessor: ({ owner }) => owner?.name,
        width: 120,
        // sortType: 'apiSort',
        disableSortBy: true,
      },
      {
        Header: t('contacts'),
        id: 'contacts',
        Cell: ({ row: { original } }: CellProps<IPointListData>) => (
          <div className={styles.contacts}>
            <span className={styles.contactsPhone}>{original.phone}</span>
            <span className={styles.contactsEmail}>{original.email}</span>
          </div>
        ),
        accessor: (row) => `${row.phone}, ${row.email}`,
        width: 120,
        sortType: 'apiSort',
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IPointListData>) => (
          <StatusCell id={id} status={status} changeStatus={changePointStatus} disabled={disabled} />
        ),
        accessor: (row) => row.status,
        width: 90,
        sortType: 'apiSort',
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original: { id },
          },
        }: CellProps<IPointListData>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button className={styles.editBtn} onClick={() => onEditClick(id)}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='edit'
              place='top'
            />
          </div>
        ),
        width: 90,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    [points, disabled]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      {points && points.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...points]}
          simpleList
          isPaginated={points.length > PAGE_SIZE}
          isManualSorting
          defaultSortId={pointsQuery.orderBy}
          defaultSortOrder={pointsQuery.orderDirection === 'DESC'}
          apiSort={(orderBy, orderDirection) =>
            (pointsQuery?.orderBy !== orderBy || pointsQuery?.orderDirection !== orderDirection) &&
            mutate({ category: pointsQuery.category, orderBy, orderDirection })
          }
          addData={(offset) => mutate({ category: pointsQuery.category, offset, limit: POINTS_TABLE_LIMIT })}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
};

export default observer(PointsTable);
